// External Imports
import { Box, font, Inline, pxToRem, Stack, styled, Text } from '@lessonnine/design-system.lib';
import get from 'lodash/fp/get';
import React from 'react';
import Helmet from 'react-helmet';
import { connect } from 'react-redux';

import { breakpoints } from '../../../config/css/breakpoints.mjs';
import { options } from '../../../config/reactRedux.mjs';
import { CONTENT_ARCHIVE } from '../../../config/types/prop.mjs';
import { ListViewContext } from '../../../context/listView.mjs';
import { useAccessibilityFocusLastAfterLoading } from '../../../hooks/useAccessibilityFocusLastAfterLoading.mjs';
import { createSelectTranslation } from '../../../selectors/createSelectTranslation.mjs';
import { ContentList } from '../../ContentList/ContentList.jsx';
import { Divider } from '../../Divider/Divider.jsx';
import { I18n } from '../../I18n/I18n.jsx';
import { Meta } from '../../Meta/Meta.jsx';
import { ShowMoreButton } from '../../ShowMoreButton/ShowMoreButton.jsx';
// Internal Imports
import { AuthorHeader } from './Header/AuthorHeader.jsx';

// Local Variables
const ArticlesHeader = styled(Text)`
  ${font({ fontSize: 24, lineHeight: 29 / 24, type: 'expressive' })};
`;

const NoArticlesText = styled(Text)`
  font-size: ${pxToRem(16)};
  line-height: 1.4;

  @media ${breakpoints.mobile.max} {
    font-size: ${pxToRem(14)};
    line-height: ${20 / 14};
  }
`;

// Component Definition
function ContentArchiveAuthorBase({
  currentEdition,
  getTranslation,
  items,
  loadingMore,
  onLoadMore,
  pagination: { hasMore },
}) {
  const onFocus = useAccessibilityFocusLastAfterLoading({ loading: loadingMore });
  const showLoadMore = hasMore || loadingMore;
  const hasItems = items.length > 0;
  const author = get('0.metadata.coauthors.0', items);
  const {
    absolutePath: authorAbsolutePath,
    displayName,
    firstName,
    relativePath: authorRelativePath,
  } = author || {};

  const magazineName = getTranslation('meta-title', 'Babbel Magazine');
  const titleText = `${displayName} - ${magazineName}`;

  return (
    <>
      <Meta title={titleText} />
      <Helmet>
        <link href={authorRelativePath} rel="author" />
        <link href={authorAbsolutePath} rel="canonical" />
      </Helmet>
      <Stack as="section">
        {author && (
          <>
            {/* eslint-disable-next-line react/jsx-props-no-spreading -- TODO: remove this potentially-dangerous spread */}
            <AuthorHeader {...author} currentEdition={currentEdition} />
            <Divider shouldShowOnMobile />
          </>
        )}
        <ArticlesHeader>
          <I18n defaultText="Articles by" field="articles-by" />
          &nbsp;{firstName}
        </ArticlesHeader>
        {/* TODO [redesign]: invisible divider here is meant to take the space, after rebranding should be replaced by gap */}
        <Divider isInvisible />
        {hasItems ? (
          <ListViewContext.Provider value="threeUp">
            <ContentList
              interstitials={[
                {
                  content: <Divider />,
                  count: Number.POSITIVE_INFINITY,
                  frequency: 3,
                },
              ]}
              items={items}
              onFocus={onFocus}
            />
          </ListViewContext.Provider>
        ) : (
          <Inline align="center" padding={[50, 0]}>
            <Box>
              <NoArticlesText>No articles</NoArticlesText>
            </Box>
          </Inline>
        )}
        <Divider />
        {showLoadMore && (
          <ShowMoreButton disabled={loadingMore} loading={loadingMore} onClick={onLoadMore} />
        )}
      </Stack>
    </>
  );
}

ContentArchiveAuthorBase.propTypes = CONTENT_ARCHIVE;

const mapStateToProps = (state) => ({
  getTranslation: createSelectTranslation(state),
});

const withRedux = connect(mapStateToProps, undefined, undefined, options);
const ContentArchiveAuthor = withRedux(ContentArchiveAuthorBase);

// Module Exports
export { ContentArchiveAuthor };
